/**
 * Central permission catalog (M2.2, ARCHITECTURE.md §5.2, API.md §1.11).
 *
 * This file is the machine-readable transcription of the roles × permissions matrix
 * in `Docs/SECURITY.md` §5.1. The matrix there is the design of record; this is the
 * enforcement. The two are kept in step by `apps/api/test/auth/permissions.spec.ts`,
 * which parses the Markdown table and fails when a cell here disagrees with a cell
 * there - so a permission cannot be widened in code without widening it in the
 * document a reviewer reads. (This comment named `apps/api/test/authz/matrix.spec.ts`
 * until M13.6; no such file exists. `apps/api/test/authz/matrix.int.spec.ts` is the
 * different, live-HTTP matrix, which produces `authz/report.md`.)
 *
 * ## Reading the matrix
 *
 * SECURITY.md marks cells `Y`, `R`, `O`, `I` or blank. Only two of those distinctions
 * are the guard's business:
 *
 * | Cell | Meaning | Here |
 * |---|---|---|
 * | `Y` | granted | in the role's set |
 * | `O` | granted, own records only | in the role's set; ownership is a **service-level** check (`patient_id = session.patientId`) |
 * | `R` | granted, subject to the charge-review rule | in the role's set; the amount check is a **service-level** rule (M7.3) |
 * | `I` | granted only with an explicit tenant scope (`X-Tenant-Id`) and audited | in the role's set; the scope check is `PlatformScopeGuard` |
 * | blank | denied | absent |
 *
 * Keeping `O`, `R` and `I` out of the guard is deliberate. A guard that answered
 * "maybe, depending on the row" would have to load the row, and an authorization
 * layer that loads rows is one that can be made to leak their existence. The guard
 * answers the question it can answer from the role alone; everything narrower is a
 * check the service does with the record already in hand.
 *
 * ## Adding a permission
 *
 * 1. Add the row to SECURITY.md §5.1 first.
 * 2. Add the key to `PERMISSIONS` and to every granting role in `ROLE_PERMISSIONS`.
 * 3. `pnpm --filter @platform/api test` - the matrix test proves the two agree.
 */
/**
 * Every permission key the platform knows about, grouped as in SECURITY.md §5.1.
 * `as const` makes the array the source of the `Permission` union: a typo in a
 * `@RequirePermission()` is a compile error, not a route that silently denies.
 */
export const PERMISSIONS = [
    // --- Portal (patient, own records only) ---------------------------------
    'portal.appointments.read',
    'portal.appointments.reschedule',
    'portal.appointments.cancel',
    'portal.intake.submit',
    'portal.payment_method.update',
    'portal.profile.read',
    'portal.profile.update',
    'portal.notifications.read',
    'portal.waitlist.manage',
    // --- Appointments --------------------------------------------------------
    'appointments.read',
    'appointments.create',
    'appointments.update',
    'appointments.reschedule',
    'appointments.cancel',
    'appointments.mark_completed',
    'appointments.export',
    // --- Patients ------------------------------------------------------------
    'patients.read',
    'patients.create',
    'patients.update',
    'patients.merge',
    'patients.anonymize',
    'patients.export',
    // --- Scheduling ----------------------------------------------------------
    'providers.read',
    'providers.manage',
    'schedules.read',
    'schedules.manage',
    'appointment_types.manage',
    'booking_settings.manage',
    // --- Insurance and pricing ----------------------------------------------
    'insurance.read',
    'insurance.check',
    'insurance.review',
    'pricing.read',
    'pricing.manage',
    'ai_estimates.read',
    'ai_estimates.run',
    // --- Payments ------------------------------------------------------------
    'payments.read',
    'payments.charge',
    'payments.adjust',
    'payments.void',
    'payments.refund',
    'payment_methods.manage',
    // --- Intake and files ----------------------------------------------------
    'intake.read',
    'intake.assign',
    'intake.review',
    'forms.read',
    'forms.manage',
    'forms.publish',
    'files.read',
    'files.upload',
    'files.delete',
    // --- Notifications -------------------------------------------------------
    'notifications.read',
    'notifications.resend',
    'templates.manage',
    'notification_settings.manage',
    // --- Integrations (tenant) ----------------------------------------------
    'integrations.read',
    'integrations.connect',
    'integrations.rotate_credentials',
    'integrations.disable',
    'integrations.test',
    // --- Reports -------------------------------------------------------------
    'reports.funnel',
    'reports.finance',
    'reports.communications',
    'reports.integrations',
    'reports.export',
    // --- Logs and audit ------------------------------------------------------
    'logs.read',
    'audit.read',
    // --- Tenant settings -----------------------------------------------------
    'tenant.branding.manage',
    'tenant.domains.manage',
    'tenant.users.read',
    'tenant.users.manage',
    'tenant.roles.assign',
    'tenant.settings.manage',
    // --- Feedback ------------------------------------------------------------
    'feedback.submit',
    'feedback.moderate',
    // --- Platform (super admin only) ----------------------------------------
    'platform.tenant.read',
    'platform.tenant.manage',
    'platform.plans.manage',
    'platform.features.manage',
    'platform.catalog.manage',
    'platform.integrations.enable',
    /**
     * ADR-033 (M16.1): set, replace, remove and test a tenant's integration credentials
     * from the super console. platform_admin only; the routes add step-up and refuse an
     * impersonated session.
     */
    'platform.integrations.manage',
    'platform.users.read',
    'platform.users.manage',
    'platform.superadmin.promote',
    'platform.logs.read',
    'platform.health.read',
    'platform.impersonate',
    'platform.settings.manage',
];
const PERMISSION_SET = new Set(PERMISSIONS);
/** Type guard for a value that claims to be a permission key. */
export function isPermission(value) {
    return PERMISSION_SET.has(value);
}
/** Role keys from ARCHITECTURE.md §5.2. */
export const ROLES = [
    'patient',
    'clinic_staff',
    'clinic_admin',
    'platform_support',
    'platform_admin',
];
const ROLE_SET = new Set(ROLES);
export function isRole(value) {
    return ROLE_SET.has(value);
}
/** Roles that act on the platform surface and carry no tenant membership. */
export const PLATFORM_ROLES = ['platform_support', 'platform_admin'];
export function isPlatformRole(value) {
    return value === 'platform_support' || value === 'platform_admin';
}
/** Roles that act inside one tenant. */
export const TENANT_ROLES = ['patient', 'clinic_staff', 'clinic_admin'];
/**
 * `portal.*` keys, held only by patients and always scoped to their own records
 * (the `O` column of SECURITY.md §5.1). Listed once so the ownership rule can be
 * asserted in one place rather than re-derived per endpoint.
 */
const PATIENT_PERMISSIONS = [
    'portal.appointments.read',
    'portal.appointments.reschedule',
    'portal.appointments.cancel',
    'portal.intake.submit',
    'portal.payment_method.update',
    'portal.profile.read',
    'portal.profile.update',
    'portal.notifications.read',
    'portal.waitlist.manage',
    // `O` in the files rows: a patient may read and upload their own files.
    'files.read',
    'files.upload',
    'feedback.submit',
];
/**
 * Everything `clinic_staff` may do. `payments.charge`, `payments.adjust` and
 * `payments.void` are the `R` cells: granted here, then narrowed by the charge
 * review rule in the payments service (SECURITY.md §5.1 notes, M7.3).
 */
const CLINIC_STAFF_PERMISSIONS = [
    'appointments.read',
    'appointments.create',
    'appointments.update',
    'appointments.reschedule',
    'appointments.cancel',
    'appointments.mark_completed',
    'patients.read',
    'patients.create',
    'patients.update',
    'providers.read',
    'schedules.read',
    'insurance.read',
    'insurance.check',
    'insurance.review',
    'pricing.read',
    'ai_estimates.read',
    'ai_estimates.run',
    'payments.read',
    'payments.charge',
    'payments.adjust',
    'payments.void',
    'payment_methods.manage',
    'intake.read',
    'intake.assign',
    'intake.review',
    'forms.read',
    'files.read',
    'files.upload',
    'notifications.read',
    'notifications.resend',
    'reports.funnel',
    'reports.communications',
    'feedback.submit',
];
/**
 * `clinic_admin` is a strict superset of `clinic_staff` (SECURITY.md §5.1: every `Y`
 * in the staff column is also a `Y` in the admin column), plus the configuration,
 * money and user-management keys. The superset relation is asserted by the tests.
 */
const CLINIC_ADMIN_ONLY_PERMISSIONS = [
    'appointments.export',
    'patients.merge',
    'patients.anonymize',
    'patients.export',
    'providers.manage',
    'schedules.manage',
    'appointment_types.manage',
    'booking_settings.manage',
    'pricing.manage',
    'payments.refund',
    'forms.manage',
    'forms.publish',
    'files.delete',
    'templates.manage',
    'notification_settings.manage',
    'integrations.read',
    'integrations.connect',
    'integrations.rotate_credentials',
    'integrations.disable',
    'integrations.test',
    'reports.finance',
    'reports.integrations',
    'reports.export',
    'logs.read',
    'audit.read',
    'tenant.branding.manage',
    'tenant.domains.manage',
    'tenant.users.read',
    'tenant.users.manage',
    'tenant.roles.assign',
    'tenant.settings.manage',
    'feedback.moderate',
];
/**
 * The `I` cells: cross-tenant reads a platform role may perform, each of which needs
 * an explicit `X-Tenant-Id` on `/api/v1/platform/*` and writes an audit row
 * (SECURITY.md §6.2). The guard grants the key; `PlatformScopeGuard` enforces the
 * header and the audit.
 */
const PLATFORM_CROSS_TENANT_READ = [
    'appointments.read',
    'patients.read',
    'providers.read',
    'schedules.read',
    'insurance.read',
    'pricing.read',
    'ai_estimates.read',
    'payments.read',
    'intake.read',
    'forms.read',
    'notifications.read',
    'integrations.read',
    'integrations.test',
    'reports.funnel',
    'reports.finance',
    'reports.communications',
    'reports.integrations',
    'logs.read',
    'audit.read',
    'tenant.users.read',
];
/** `platform_support`: read across tenants, impersonate, no platform writes. */
const PLATFORM_SUPPORT_PERMISSIONS = [
    ...PLATFORM_CROSS_TENANT_READ,
    'platform.tenant.read',
    'platform.users.read',
    'platform.logs.read',
    'platform.health.read',
    'platform.impersonate',
    'feedback.submit',
];
/**
 * `platform_admin`: everything support may do, plus the platform writes and the four
 * tenant-scoped `I` writes SECURITY.md grants it (`integrations.disable`,
 * `tenant.domains.manage`, `tenant.users.manage`, `tenant.roles.assign`,
 * `tenant.settings.manage`). Those writes are still refused while impersonating.
 */
const PLATFORM_ADMIN_PERMISSIONS = [
    ...PLATFORM_SUPPORT_PERMISSIONS,
    'integrations.disable',
    'tenant.domains.manage',
    'tenant.users.manage',
    'tenant.roles.assign',
    'tenant.settings.manage',
    'platform.tenant.manage',
    'platform.plans.manage',
    'platform.features.manage',
    'platform.catalog.manage',
    'platform.integrations.enable',
    'platform.integrations.manage',
    'platform.users.manage',
    'platform.superadmin.promote',
    'platform.settings.manage',
    'feedback.moderate',
];
function frozenSet(keys) {
    return new Set(keys);
}
/**
 * Role → permissions, as sets. A `Set` rather than an array because
 * `hasPermission()` is called once per request per required key and a linear scan of
 * ninety strings on every admin request is a cost with no upside.
 */
export const ROLE_PERMISSION_SETS = Object.freeze({
    patient: frozenSet(PATIENT_PERMISSIONS),
    clinic_staff: frozenSet(CLINIC_STAFF_PERMISSIONS),
    clinic_admin: frozenSet([...CLINIC_STAFF_PERMISSIONS, ...CLINIC_ADMIN_ONLY_PERMISSIONS]),
    platform_support: frozenSet(PLATFORM_SUPPORT_PERMISSIONS),
    platform_admin: frozenSet(PLATFORM_ADMIN_PERMISSIONS),
});
/**
 * Role → permissions as sorted arrays. This is the shape `GET /auth/session` returns
 * to the SPAs so they can hide controls the caller cannot use - display only; the
 * server never trusts it back (rules §17.2, §22.2).
 */
export const ROLE_PERMISSIONS = Object.freeze(Object.fromEntries(ROLES.map((role) => [role, Object.freeze([...ROLE_PERMISSION_SETS[role]].sort())])));
/**
 * The authorization question, answered from the role alone.
 *
 * Default deny in three ways: an unknown role has no set, an unknown key is not in
 * any set, and there is no wildcard - `platform_admin` is enumerated like everyone
 * else, so a new permission is denied to every role until someone grants it.
 */
export function hasPermission(role, permission) {
    if (!isRole(role))
        return false;
    if (!isPermission(permission))
        return false;
    return ROLE_PERMISSION_SETS[role].has(permission);
}
/** True when the role holds every key in `permissions` (the comma rule of API.md §1.11). */
export function hasAllPermissions(role, permissions) {
    return permissions.every((permission) => hasPermission(role, permission));
}
/** True when the role holds at least one key (the `|` rule of API.md §1.11). */
export function hasAnyPermission(role, permissions) {
    return permissions.some((permission) => hasPermission(role, permission));
}
/** The caller's keys, for `GET /auth/session`. Empty for an unknown role. */
export function permissionsForRole(role) {
    return isRole(role) ? ROLE_PERMISSIONS[role] : [];
}
/**
 * Actions refused while a super admin is impersonating a tenant user
 * (SECURITY.md §6.3, ADR-005). Money, exports and credentials: the three things a
 * support session must never be able to do on someone's behalf.
 *
 * The list is here rather than in the API so the super admin SPA can grey the same
 * controls out from the same source. The decision is still made server-side by
 * `@BlockedWhileImpersonating()`; this is what that decorator defaults to.
 */
export const IMPERSONATION_BLOCKED_PERMISSIONS = [
    'payments.charge',
    'payments.adjust',
    'payments.void',
    'payments.refund',
    'payment_methods.manage',
    'patients.export',
    'appointments.export',
    'reports.export',
    'integrations.connect',
    'integrations.rotate_credentials',
    'tenant.users.manage',
    'tenant.roles.assign',
];
const IMPERSONATION_BLOCKED_SET = new Set(IMPERSONATION_BLOCKED_PERMISSIONS);
export function isBlockedWhileImpersonating(permission) {
    return IMPERSONATION_BLOCKED_SET.has(permission);
}
//# sourceMappingURL=permissions.js.map